import * as React from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import MHODateTime from 'domain/dateTime/MHODateTime';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FlexContainer from 'global_elements/Layouts/FlexContainer';
import InlineText from 'global_elements/Text/InlineText';
import { ReactComponent as SortIcon } from 'icons/mho-sort-icon-white.svg';

import { EmailedAssessmentTableProps, EmailedAssessmentRow } from 'types/tableProps';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';

import { HoverTableStyles } from '../styles';
import Utilities from '../../../../api/lib/Utilities';

const NoDataComponent: React.JSX.Element = <InlineText text="No Results Found." fontColor={FontColors.PRIMARY} fontSize={FontSizes.LARGE} />;

export enum EmailedAssessmentCols {
  PATIENT_NAME = 'Patient Name',
  ACCOUNT_NUMBER = 'Account Number',
  MEDICAL_RECORD = 'Medical Record',
  ADMISSION_DATE = 'Admission Date',
  DISCHARGE_DATE = 'Discharge Date',
  PROGRAM = 'Program',
  USER = 'User',
  EMAIL = 'Assessment Sent To',
  ASSESSMENT = 'Assessment',
  TIME_PERIOD = 'Assessment Time Period',
  LINK_STATUS = 'Assessment Link Status',
}

const columns: TableColumn<EmailedAssessmentRow>[] = [
  {
    name: EmailedAssessmentCols.PATIENT_NAME,
    selector: (row) => row.patientName,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.ACCOUNT_NUMBER,
    selector: (row) => row.accountNumber,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.MEDICAL_RECORD,
    selector: (row) => row.medicalRecord,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.ADMISSION_DATE,
    selector: (row) => row.admissionDate,
    sortable: true,
    format: (row) => (new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(row.admissionDate) ? new MHODateTime(row.admissionDate).getFormattedCalendarDate() : row.admissionDate),
  },
  {
    name: EmailedAssessmentCols.DISCHARGE_DATE,
    selector: (row) => row.dischargeDate,
    sortable: true,
    format: (row) => (new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/).test(row.dischargeDate) ? new MHODateTime(row.dischargeDate).getFormattedCalendarDate() : row.dischargeDate),
  },
  {
    name: EmailedAssessmentCols.PROGRAM,
    selector: (row) => row.program,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.USER,
    selector: (row) => row.user,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.EMAIL,
    selector: (row) => row.assessmentSentTo,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.ASSESSMENT,
    selector: (row) => row.assessment,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.TIME_PERIOD,
    selector: (row) => row.assessmentTimePeriod,
    sortable: true,
  },
  {
    name: EmailedAssessmentCols.LINK_STATUS,
    selector: (row) => row.assessmentLinkStatus,
    sortable: true,
  },
];

const EmailedAssessmentsTable = (props: EmailedAssessmentTableProps): React.JSX.Element => {
  let tableColumns = columns;
  if (props.getColumns) {
    tableColumns = props.getColumns(columns);
  }

  return (
    <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.CENTER} extraClasses="emailed-assessments-list">
      {props.isLoading ? (
        <div className="loading-icon-overlay-container">
          <FontAwesomeIcon icon={faSpinner} className="fa-spin loading-icon-overlay" />
        </div>
      ) : (
        <DataTable
          dense
          highlightOnHover
          persistTableHead
          pagination
          paginationPerPage={10}
          sortIcon={<SortIcon />}
          customStyles={HoverTableStyles}
          columns={tableColumns}
          data={props.data}
          noDataComponent={NoDataComponent}
          onRowClicked={props.onRowClicked}
          selectableRows={props.selectableRows}
          selectableRowSelected={props.selectableRowSelected}
          onSelectedRowsChange={props.onSelectedRowsChange}
          onChangePage={props.onChangePage}
          onSort={Utilities.setCellTitles}
        />
      )}
    </FlexContainer>
  );
};

export default EmailedAssessmentsTable;
